import * as React from "react";
import { Link } from "react-router-dom";
import { OrdersState } from "../../reducers/orders";
import { Order, OrderUpdate, PaymentInfo } from "../../models/order";
import {
  getDecryptedTransactions,
  getOrderDetail,
  resendOrderConfirmationEmail,
  OrdersAction,
  setKey,
  setErrorMessage,
  updateOrder,
} from "../../actions/orders";
import { ApplicationState } from "../../reducers";
import { connect } from "react-redux";
import {
  FormGroup,
  FormControl,
  ControlLabel,
  Row,
  Col,
} from "react-bootstrap";
import {
  ListGroup,
  ListGroupItem,
  Button,
  Table,
  Modal,
  Alert,
} from "react-bootstrap";
import { map, isEmpty, size, clone, range } from "lodash";

import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { Future, moneyFormat } from "../../utils";
import { RouteComponentProps } from "react-router";
import { TicketMap } from "../../reducers/config";
import { LoadingOverlay } from "../Loading";
import { destinationLabel } from "../../models/hotels";
import * as moment from "moment";

interface OrderDetailProps extends OrdersState, RouteComponentProps<any> {
  getOrderDetail: (id: string) => Promise<OrdersAction>;
  updateOrder: (order: OrderUpdate) => Promise<OrdersAction>;
  setKey: (key: string) => OrdersAction;
  getDecryptedTransactions: (id: string, key: string) => Promise<OrdersAction>;
  resendOrderConfirmationEmail: (id: string) => Promise<OrdersAction>;
  setErrorMessage: (msg: string) => OrdersAction;
  tickets: TicketMap;
}

interface OrderDetailState {
  keyDialogOpen: boolean;
  keyPromise?: Future<string>;
}

class OrderDetail extends React.Component<OrderDetailProps, OrderDetailState> {
  constructor(props: OrderDetailProps) {
    super(props);
    this.state = { keyDialogOpen: false };
  }

  componentDidMount(): void {
    const id = this.props.match.params.id;
    this.props.getOrderDetail(id);
  }

  componentWillUpdate(
    nextProps: Readonly<OrderDetailProps>,
    nextState: Readonly<OrderDetailState>,
    nextContext: any
  ): void {
    if (nextProps.decryptionKey && this.state.keyPromise) {
      this.state.keyPromise.resolve(nextProps.decryptionKey);
      this.setState({ keyPromise: null });
    }
  }

  public render(): JSX.Element {
    const { orderDetail } = this.props;
    if (this.props.loading) {
      return <LoadingOverlay />;
    }
    if (!orderDetail) {
      return null;
    }

    const haveCC = orderDetail.transactions.find((t) => t.haveNotes);

    return (
      <div>
        {this.props.errorMessage && (
          <Alert bsStyle="danger">{this.props.errorMessage}</Alert>
        )}
        <Row>
          <Col xs={6}>
            <Link to="/admin/orders">
              <i className="fa fa-arrow-left" /> Back to Orders
            </Link>
          </Col>
          <Col xs={6} style={{ textAlign: "right" }}>
            <Button
              style={{ marginRight: "15px" }}
              bsStyle="primary"
              onClick={() =>
                this.props.resendOrderConfirmationEmail(
                  this.props.orderDetail.id
                )
              }
            >
              Resend Confirmation Email
            </Button>
            <Button
              bsStyle="primary"
              onClick={() => this.setState({ keyDialogOpen: true })}
            >
              Set Encryption Key
            </Button>
          </Col>
        </Row>
        {this.renderOrderStatus()}
        {this.renderCustomerInfo()}
        <h3>Tickets</h3>
        {this.renderTickets()}
        <h3>Guests</h3>
        {this.renderGuests()}
        <h3>
          Transactions{" "}
          <Button disabled={!haveCC} onClick={this.getCardDetails.bind(this)}>
            Get Card Details
          </Button>
        </h3>
        {this.renderTransactions()}
        <KeyDialog
          decryptionKey={this.props.decryptionKey}
          setKey={this.props.setKey.bind(this)}
          toggleOpen={(open) => this.setState({ keyDialogOpen: open })}
          open={this.state.keyDialogOpen}
        />
      </div>
    );
  }

  private renderGuests(): JSX.Element {
    const { orderDetail } = this.props;
    return (
      <Table striped condensed>
        <thead>
          <tr>
            <th>Guest</th>
          </tr>
        </thead>
        <tbody>
          {orderDetail.guests.map((guest, index) => (
            <tr key={index}>
              <td>
                {guest.title} {guest.firstName} {guest.middleName}{" "}
                {guest.lastName} {guest.suffix}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  private renderOrderStatus(): JSX.Element {
    const { orderDetail } = this.props;
    const haveCC = orderDetail.transactions.find((t) => t.haveNotes);
    return (
      <Row>
        <h3>Status</h3>
        <Col md={3}>
          <FormGroup>
            <ControlLabel>Order Status</ControlLabel>
            <FormControl
              componentClass="select"
              value={orderDetail.orderStatus}
              onChange={(e: any) =>
                this.updateOrder(
                  (o: OrderUpdate) => (o.status = e.target.value)
                )
              }
            >
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="cancelled">Cancelled</option>
            </FormControl>
          </FormGroup>
        </Col>
        <Col md={4}>
          <Button
            bsStyle="primary"
            disabled={!haveCC}
            onClick={() => {
              if (
                confirm(
                  "Are you sure you want to delete the credit card detail? It will be gone forever!"
                )
              ) {
                this.updateOrder((o: OrderUpdate) => (o.clearCardData = true));
              }
            }}
          >
            Permanently Delete Credit Card Detail
          </Button>
        </Col>
      </Row>
    );
  }

  private renderCustomerInfo(): JSX.Element {
    const {
      orderDetail: { user, discounts },
      orderDetail,
    } = this.props;
    return (
      <Row>
        <Col md={6}>
          <h3>Order Info</h3>
          <Table striped hover>
            <tbody>
              <tr>
                <td>First Name</td>
                <td>{user.firstName}</td>
              </tr>
              <tr>
                <td>Last Name</td>
                <td>{user.lastName}</td>
              </tr>
              <tr>
                <td>Phone #</td>
                <td>{user.phoneNumber}</td>
              </tr>
              <tr>
                <td>Email</td>
                <td>{user.email}</td>
              </tr>
              <tr>
                <td>Shipping Method</td>
                <td>{orderDetail.shippingMethod}</td>
              </tr>
              <tr>
                <td># Tickets Insured</td>
                <td>{orderDetail.insuranceTickets}</td>
              </tr>
              <tr>
                <td>Ticket Total</td>
                <td>{moneyFormat(orderDetail.subTotal)}</td>
              </tr>
              <tr>
                <td>Shipping Total</td>
                <td>{moneyFormat(orderDetail.shippingTotal)}</td>
              </tr>
              <tr>
                <td>Insurance Total</td>
                <td>{moneyFormat(orderDetail.insuranceTotal)}</td>
              </tr>
              <tr>
                <td>Memory Maker</td>
                <td>{moneyFormat(orderDetail.memoryMakerTotal)}</td>
              </tr>
              {/* <tr>
                                <td>Genie+</td>
                                <td>{moneyFormat(orderDetail.geniePlusTotal)}</td>
                            </tr> */}
              <tr>
                <td>Discount</td>
                <td>{moneyFormat(orderDetail.discountTotal)}</td>
              </tr>
              <tr>
                <td>Grand Total</td>
                <td>{moneyFormat(orderDetail.grandTotal)}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
        <Col md={6}>
          {!orderDetail.shippingAddress ? null : (
            <div>
              <h3>Shipping Address</h3>
              <div className={"address"}>
                <div>{orderDetail.shippingAddress.address1}</div>
                <div>{orderDetail.shippingAddress.address2}</div>
                <div>
                  {orderDetail.shippingAddress.city}{" "}
                  {orderDetail.shippingAddress.state},{" "}
                  {orderDetail.shippingAddress.zip}
                </div>
              </div>
            </div>
          )}

          {!orderDetail.billingAddress ? null : (
            <div>
              <h3>Billing Address</h3>
              <div className={"address"}>
                <div>{orderDetail.billingAddress.address1}</div>
                <div>{orderDetail.billingAddress.address2}</div>
                <div>
                  {orderDetail.billingAddress.city}{" "}
                  {orderDetail.billingAddress.state},{" "}
                  {orderDetail.billingAddress.zip}
                </div>
              </div>
            </div>
          )}
          <h3>Discounts</h3>
          <ListGroup>
            {discounts.map((discount) => (
              <ListGroupItem key={discount.discountId}>
                {discount.discountName}
              </ListGroupItem>
            ))}
          </ListGroup>
        </Col>
      </Row>
    );
  }

  private renderHotels(): JSX.Element {
    const { orderDetail } = this.props;

    let hotels = (
      <ListGroup>
        <ListGroupItem>Order has no hotels</ListGroupItem>
      </ListGroup>
    );
    if (!isEmpty(orderDetail.hotel)) {
      hotels = (
        <Table striped hover className="cart-hotels">
          <thead>
            <tr>
              <th>Hotel</th>
              <th>Dates</th>
              <th>Occupants</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {map(orderDetail.hotel, (orderHotel, index) => {
              return (
                <tr key={index}>
                  <td>
                    <Link to={`/hotels/${orderHotel.hotelId}`} target="_blank">
                      {orderHotel.hotelTitle}
                    </Link>
                    <br />
                    {orderHotel.roomTitle}
                  </td>
                  <td>
                    <div>
                      <label>Check-in</label>
                      <div>{orderHotel.checkIn}</div>
                    </div>
                    <div>
                      <label>Check-out</label>
                      <div>{orderHotel.checkOut}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <label>Adults</label>
                      <div>{orderHotel.numAdults}</div>
                    </div>
                    <div>
                      <label>Children</label>
                      <div>{size(orderHotel.childAges)}</div>
                    </div>
                  </td>
                  <td className="actions">
                    {
                      <Button
                        className="hint--bottom"
                        onClick={() => orderHotel}
                        aria-label="Remove Hotel"
                      >
                        <i className="fa fa-remove" />
                      </Button>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      );
    }
    return hotels;
  }

  private renderTickets(): JSX.Element {
    const { orderDetail } = this.props;

    let tickets = (
      <ListGroup>
        <ListGroupItem>Your package has no tickets</ListGroupItem>
      </ListGroup>
    );
    if (!isEmpty(orderDetail.tickets)) {
      tickets = (
        <Table striped hover>
          <thead>
            <tr>
              <th>Ticket</th>
              <th># Adults</th>
              <th># Children</th>
              <th>First Date</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {map(orderDetail.tickets, (orderTicket, index) => {
              const ticket = this.props.tickets[orderTicket.ticketId];
              let ticketLabel = "";
              if (ticket) {
                ticketLabel = `${destinationLabel(ticket.destination)} ${
                  ticket.days
                }-Day ${ticket.name}`;
              }
              return (
                <tr key={index}>
                  <td>{ticketLabel}</td>
                  <td>{orderTicket.quantityAdults}</td>
                  <td>{orderTicket.quantityChildren}</td>
                  <td>{orderTicket.firstDate}</td>
                  <td className="actions"></td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      );
    }
    return tickets;
  }

  private renderTransactions(): JSX.Element {
    const { orderDetail } = this.props;

    let transactionsUI = (
      <ListGroup>
        <ListGroupItem>Your package has no transactions</ListGroupItem>
      </ListGroup>
    );

    let transactions = orderDetail.transactions;
    if (this.props.decryptedTransactions) {
      transactions = this.props.decryptedTransactions.map((tx) => {
        try {
          const pmtInfo = JSON.parse(tx.notes) as PaymentInfo;
          if (pmtInfo) {
            tx = clone(tx);
            tx.lastFour = pmtInfo.ccNum;
            tx.cvv2 = pmtInfo.cvv2;
          }
        } catch (e) {
          this.props.setErrorMessage("Failed decrypting transaction " + e);
        }
        return tx;
      });
    }

    if (!isEmpty(transactions)) {
      transactionsUI = (
        <Table striped hover>
          <thead>
            <tr>
              <th>Amount</th>
              <th>Card Number</th>
              <th>Card CVV</th>
              <th>Card Exp</th>
              <th>Card First Name</th>
              <th>Card Last Name</th>
              <th>Card Type</th>
              <th>Card Zip</th>
            </tr>
          </thead>
          <tbody>
            {map(transactions, (trans, index) => {
              return (
                <tr key={index}>
                  <td>{moneyFormat(trans.amount)}</td>
                  <td>{trans.lastFour}</td>
                  <td>{trans.cvv2 || ""}</td>
                  <td>
                    {trans.cardExpMonth}/{trans.cardExpYear}
                  </td>
                  <td>{trans.cardFirstName}</td>
                  <td>{trans.cardLastName}</td>
                  <td>{trans.cardType}</td>
                  <td>{trans.cardZip}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      );
    }
    return transactionsUI;
  }

  private getCardDetails() {
    const id = this.props.orderDetail
      ? this.props.orderDetail.id
      : this.props.match.params.id;
    this.ensureKey().then((key) =>
      this.props.getDecryptedTransactions(id, key)
    );
  }

  private ensureKey(): Promise<string> {
    if (this.props.decryptionKey) {
      return Promise.resolve(this.props.decryptionKey);
    }
    const future = new Future<string>();
    this.setState({ keyPromise: future, keyDialogOpen: true });
    return future.promise;
  }

  private updateOrder(fn: (order: OrderUpdate) => void) {
    const order = this.props.orderDetail;
    const update = { orderId: order.id } as OrderUpdate;
    fn(update);
    this.props.updateOrder(update);
  }
}

const connected = connect(
  (state: ApplicationState) => ({
    ...state.orders,
    tickets: state.config.tickets,
  }),
  {
    getOrderDetail,
    setKey,
    getDecryptedTransactions,
    setErrorMessage,
    resendOrderConfirmationEmail,
    updateOrder,
  }
)(OrderDetail);

export default connected;

interface KeyDialogProps {
  decryptionKey: string;
  open: boolean;
  setKey: (k: string) => void;
  toggleOpen: (open: boolean) => void;
}

interface KeyDialogState {
  decryptionKey: string;
}

class KeyDialog extends React.Component<KeyDialogProps, KeyDialogState> {
  constructor(props: KeyDialogProps) {
    super(props);
    this.state = { decryptionKey: props.decryptionKey };
  }
  public render(): JSX.Element {
    return (
      <Modal show={this.props.open} onHide={() => this.props.toggleOpen(false)}>
        <Modal.Header>
          <Modal.Title>Encryption Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup>
            <ControlLabel>Key</ControlLabel>
            <FormControl
              componentClass="textarea"
              value={this.state.decryptionKey}
              onChange={(e: any) =>
                this.setState({ decryptionKey: e.target.value })
              }
            />
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            bsStyle="success"
            onClick={() => {
              this.props.setKey(this.state.decryptionKey);
              this.props.toggleOpen(false);
            }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/admin/OrderDetail.tsx